import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { API, resolveImg } from "@/App";
import { safeHtml } from "@/lib/sanitize";
import SafeImg from "@/components/SafeImg";
import Seo from "@/components/Seo";

function stripHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export default function GuestDetail() {
  const { slug } = useParams();
  const [guest, setGuest] = useState(null);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setGuest(null); setError(null);
    axios.get(`${API}/guests/${slug}`)
      .then((r) => setGuest(r.data))
      .catch(() => setError("No se encontró el invitado"));
    axios.get(`${API}/guests`).then((r) => {
      const all = r.data;
      const idx = all.findIndex((x) => x.slug === slug);
      // Get neighbors
      const neighbors = [];
      if (idx > 0) neighbors.push(all[idx - 1]);
      if (idx < all.length - 1) neighbors.push(all[idx + 1]);
      // Random 6 others from same year or recent
      const pool = all.filter((x) => x.slug !== slug);
      const sameYear = guest?.year ? pool.filter((x) => x.year === guest.year) : [];
      const rest = pool.filter((x) => !sameYear.includes(x));
      setRelated([...sameYear, ...rest].slice(0, 8));
    });
    window.scrollTo(0, 0);
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <p className="eyebrow">Error</p>
        <h1 className="font-serif text-4xl mt-4">{error}</h1>
        <Link to="/invitados" className="btn-ghost mt-8 inline-flex">← Volver al archivo</Link>
      </div>
    );
  }
  if (!guest) return <div className="max-w-xl mx-auto px-6 py-20 text-[var(--text-muted)]">Cargando…</div>;

  const eventPhotos = guest.event_photos || [];
  const cleanBio = stripHtml(guest.bio_html || "").slice(0, 180);
  const seoDesc = `${guest.name}${guest.role ? ` · ${guest.role}` : ""}. ${cleanBio || "Invitado de honor de la Tertulia In Dubio, Sevilla."}`.slice(0, 300);
  const ogImg = guest.image && guest.image.startsWith("http") ? guest.image : guest.image ? guest.image : undefined;
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: guest.name,
    description: cleanBio || guest.role,
    jobTitle: guest.role || undefined,
    image: guest.image ? (guest.image.startsWith("http") ? guest.image : `https://tertuliaindubio.es${guest.image}`) : undefined,
    subjectOf: {
      "@type": "Event",
      name: `Tertulia In Dubio con ${guest.name}`,
      startDate: guest.visit_date || undefined,
      location: { "@type": "Place", name: "Sevilla", address: "Sevilla, España" },
      organizer: { "@id": "https://tertuliaindubio.es/#organization" },
    },
  };

  return (
    <article data-testid={`guest-detail-${guest.slug}`}>
      <Seo
        title={guest.name}
        description={seoDesc}
        path={`/invitados/${guest.slug}`}
        image={ogImg}
        type="profile"
        schema={personSchema}
      />
      {/* Hero section with large portrait */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-6 pb-4">
          <Link to="/invitados" className="text-[11px] uppercase tracking-[0.24em] text-[var(--text-muted)] hover:text-[var(--text-ink)]" data-testid="back-to-guests">
            ← Archivo de invitados
          </Link>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-10 pt-4 grid lg:grid-cols-[460px_1fr] gap-10 lg:gap-16">
          <aside className="anim-fadeup">
            <div className="relative aspect-[4/5] portrait-frame border border-[rgba(255,255,255,0.7)] shadow-[0_30px_70px_-25px_rgba(22,40,60,0.3)]">
              <SafeImg src={guest.image} alt={guest.name} name={guest.name} style={{filter:"saturate(0.95) contrast(1.04)"}} />
            </div>
            {guest.visit_date && (
              <div className="mt-5 flex items-center gap-4 border-t border-[var(--line)] pt-4">
                <div className="font-serif text-5xl text-[var(--gold-deep)] leading-none">{guest.year}</div>
                <div>
                  <div className="eyebrow mb-0.5">Visita a la tertulia</div>
                  <div className="font-serif text-lg text-[var(--text-ink)]">{guest.visit_date}</div>
                </div>
              </div>
            )}
          </aside>

          <div className="anim-fadeup" style={{animationDelay:"0.1s"}}>
            <span className="eyebrow">Invitado de honor</span>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[80px] mt-3 leading-[0.92]">{guest.name}</h1>
            <p className="text-[15px] md:text-[17px] text-[var(--sky-deep)] italic mt-5 font-light max-w-3xl leading-relaxed">
              {guest.role}
            </p>
            <div className="hairline my-8"></div>
            <div className="prose-bio max-w-2xl" dangerouslySetInnerHTML={safeHtml(guest.bio_html || "<p>Biografía no disponible.</p>")} />
          </div>
        </div>
      </section>

      {/* Media section - full-width with blue band like original */}
      {guest.featured_media && guest.media_image && (
        <section
          className="py-14 md:py-20 mt-6"
          style={{ background: "linear-gradient(135deg, rgba(63,122,153,0.95) 0%, rgba(90,145,171,0.95) 100%)", color: "#fff" }}
          data-testid="guest-media-section"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid md:grid-cols-[1fr_1.2fr] gap-10 items-center">
            <div className="bg-white p-3 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.4)] border border-white/20">
              <SafeImg src={guest.media_image} alt={`${guest.name} en los medios`} name={guest.name} className="w-full h-auto" />
            </div>
            <div>
              <span className="text-[11px] tracking-[0.3em] uppercase text-white/70">Apareció en prensa</span>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-none">{guest.name}<br/>en los medios.</h2>
              {guest.media_subtitle && (
                <p className="mt-5 text-white/85 text-[15px] leading-relaxed font-light max-w-xl">
                  {guest.media_subtitle}
                </p>
              )}
              <Link to="/en-los-medios" className="inline-block mt-8 text-[11px] uppercase tracking-[0.24em] border-b border-white/60 pb-0.5 hover:border-white">
                Ver archivo de prensa completo →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Event photos (fotos de la tertulia) */}
      {eventPhotos.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-16" data-testid="event-photos">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <span className="eyebrow">Crónica de la tertulia</span>
              <h3 className="font-serif text-4xl mt-2">La comida.</h3>
              <p className="text-[13px] text-[var(--text-muted)] mt-2 max-w-xl">
                Momentos del encuentro con {guest.name.split(" ")[0]} en nuestra mesa. Haz click en cualquier foto para ampliarla.
              </p>
            </div>
            <span className="text-[11px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
              {eventPhotos.length} {eventPhotos.length === 1 ? "foto" : "fotos"}
            </span>
          </div>
          <div className={`grid gap-3 md:gap-4 ${eventPhotos.length === 1 ? "" : eventPhotos.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
            {eventPhotos.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightbox({ image: p, caption: `${guest.name} · ${guest.visit_date || ""}` })}
                className="relative aspect-[4/3] overflow-hidden border border-[var(--line)] shadow-[0_14px_40px_-20px_rgba(22,40,60,0.22)] hover:shadow-[0_30px_70px_-25px_rgba(22,40,60,0.35)] transition-all duration-500 group cursor-zoom-in"
                data-testid={`event-photo-${i}`}
              >
                <img src={resolveImg(p)} alt={`Tertulia con ${guest.name} ${i+1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <span className="absolute top-3 right-3 px-2 py-1 bg-white/90 text-[9px] uppercase tracking-[0.22em] text-[var(--text-ink)] opacity-0 group-hover:opacity-100 transition-opacity">
                  ⤢ Ampliar
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <span className="eyebrow">Continúa explorando</span>
              <h3 className="font-serif text-4xl mt-2">Otros invitados.</h3>
            </div>
            <Link to="/invitados" className="text-[11px] uppercase tracking-[0.22em] border-b border-[var(--gold)] pb-0.5">Ver todos →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {related.map((g) => (
              <Link key={g.id} to={`/invitados/${g.slug}`} className="group block" data-testid={`related-${g.slug}`}>
                <div className="relative aspect-[3/4] overflow-hidden border border-[rgba(255,255,255,0.7)] shadow-[0_14px_40px_-20px_rgba(22,40,60,0.22)] transition-all duration-500 group-hover:shadow-[0_30px_70px_-25px_rgba(22,40,60,0.32)]">
                  <SafeImg src={g.image} alt={g.name} name={g.name} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.05]" style={{filter:"saturate(0.88)"}} />
                  <div className="absolute inset-0 z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ boxShadow:"inset 0 0 0 1px rgba(201,167,74,0.7)" }}></div>
                </div>
                <h4 className="font-serif text-lg mt-3 leading-tight group-hover:text-[var(--gold-deep)] transition-colors">{g.name}</h4>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] mt-1 line-clamp-1">{g.year} · {g.role}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox for event photos */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={() => setLightbox(null)}
          data-testid="event-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 md:top-8 md:right-8 text-white/80 hover:text-white text-3xl leading-none w-12 h-12 flex items-center justify-center border border-white/30 rounded-full"
            aria-label="Cerrar"
          >×</button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-6xl max-h-[92vh] flex flex-col items-center">
            <img src={resolveImg(lightbox.image)} alt="" className="max-w-full max-h-[80vh] object-contain shadow-[0_40px_100px_-30px_rgba(0,0,0,0.8)]" />
            {lightbox.caption && (
              <p className="mt-4 text-white/80 text-[12px] uppercase tracking-[0.24em] text-center">{lightbox.caption}</p>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
